import React, { useState, useRef, useEffect } from "react";
import { Mic, Pause, Play, Square } from "lucide-react";
import "./RecordingandUploading.css";
import CircularProgress from "@mui/material/CircularProgress";
import useUserStore from "../../store/useUserStore";
import usePatientStore from "../../store/usePatientStore";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import MicRecorder from "mic-recorder-to-mp3";
import NoSleep from "nosleep.js";
import UploadProgress from "../uploadAndRecording/UploadProgress";
import CustomPopup from "../uploadAndRecording/CustomPopup";

const Mp3Recorder = new MicRecorder({
  bitRate: 64,
  prefix: "data:audio/wav;base64,",
});

const RecordingAndUploading = ({
  patientDetail,
  setActiveComponent,
  patientId,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionEndTime, setSessionEndTime] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showProgressComponent, setShowProgressComponent] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [currentFormData, setCurrentFormData] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [uploadedBlobNames, setUploadedBlobNames] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [createConsultation, setCreateConsultation] = useState(false);

  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const timeRef = useRef(0)
  const retryCountRef = useRef(0);
  const totalChunkRef = useRef(0);
  const totalUploadedRef = useRef(0);
  const noSleep = useRef(new NoSleep());
  const chunkIntervalRef = useRef(null);
  const isUploadingRef = useRef(false);
  const temperoryRecorder = useRef(null);
  const waitingTimerRef = useRef(null);
  const { user, fetchSessions, setIsRecordingStarted } = useUserStore();
  const { setConsultationId } = usePatientStore();
  const dentistId = user?.dentist_id;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      if (waitingTimerRef.current) {
        clearTimeout(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
    };
  }, []);



  const startRecording = async () => {
    timeRef.current = 0;
    if (!isRecording) {
      recordingTimeRef.current = 0;
      setRecordingTime(0);
      if (!patientId) {
        alert("Please Enter Patient Details");
        return;
      }
      console.log("Recording started");
      setChunks([]);
      setSessionStartTime(new Date(Date.now()));
    }

    setIsPaused(false)
    Mp3Recorder.start()
      .then(() => {
        setIsRecordingStarted(true);
        setIsRecording(true);
        noSleep.current.enable();

        timerRef.current = setInterval(() => {
          setRecordingTime((prev) => {
            recordingTimeRef.current = prev + 1;
            return prev + 1;
          });
        }, 1000);

        // Generate chunks every 2 minutes (300 seconds)
        chunkIntervalRef.current = setInterval(() => {
          if (timeRef.current % 300 === 0) {
            console.log(timeRef.current);
            captureChunk();
          }
          timeRef.current += 1
        }, 1000);

        drawWaveform();
      })
      .catch((e) => console.error(e));
  };

  const captureChunk = async () => {
    if (!Mp3Recorder.activeStream) {
      console.warn("No active stream to capture chunk from");
      return;
    }

    const tempRecorder = new MicRecorder({
      bitRate: 64,
      prefix: "data:audio/wav;base64,",
    });

    temperoryRecorder.current = tempRecorder;


    try {
      // Start temporary recorder
      await tempRecorder.start();

      // Clone current recorder state
      Object.assign(tempRecorder, {
        audioContext: Mp3Recorder.audioContext,
        mic: Mp3Recorder.mic,
        processor: Mp3Recorder.processor,
        activeStream: Mp3Recorder.activeStream,
      });

      // Record for full chunk duration (300 seconds)
      await new Promise((resolve) => {
        waitingTimerRef.current = setTimeout(resolve, 300000);
      });

      if (!Mp3Recorder.activeStream) {
        console.warn("No active stream to capture chunk from");
        return;
      }

      // Stop and get the MP3
      const [buffer, blob] = await tempRecorder.stop().getMp3();
      console.log(`Captured chunk of size: ${blob.size} bytes`);

      if (blob.size > 0) {
        totalChunkRef.current += 1;
        setChunks((prev) => [...prev, { blob, timestamp: new Date() }]);
      } else {
        console.warn("Empty chunk captured");
      }
    } catch (error) {
      console.error("Chunk capture error:", error);
    } finally {
      // Clean up temporary recorder
      if (tempRecorder && tempRecorder.stop) {
        try {
          await tempRecorder.stop();
        } catch (e) {
          console.error("Error cleaning up temp recorder:", e);
        }
      }
    }
  };

  useEffect(() => {
    detectOffline();
    if (chunks.length > 0 && !isUploadingRef.current) {
      console.log("chunks length : ", chunks.length);
      uploadChunk();
    }
  }, [chunks]);

  const detectOffline = () => {
    window.addEventListener("offline", () => {
      alert("You're currently offline. Please check your internet connection.");
    });
  };

  const uploadAudioFile2 = async (formData, isRetry = false) => {
    if (!isRetry) {
      retryCountRef.current = 0;
    }
    setUploadProgress(0);
    setIsProcessing(false);
    setProcessingProgress(0);
    console.log("uploading chunk with attempt:", retryCountRef.current);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseData = JSON.parse(xhr.responseText);
            setUploadProgress(100);
            resolve(responseData.blob_name);
          } catch (error) {
            reject(error);
          }
        } else {
          if (retryCountRef.current < 2) {
            retryCountRef.current += 1;
            setTimeout(async () => {
              await uploadAudioFile2(formData, true).catch((error) => {
                reject(error);
              });
            }, 2000);
          } else {
            reject(new Error(xhr.statusText));
          }
        }
      });

      xhr.addEventListener("error", (error) => {
        if (retryCountRef.current < 2) {
          retryCountRef.current += 1;
          setTimeout(async () => {
            await uploadAudioFile2(formData, true).catch((error) => {
              reject(error);
            });
          }, 2000);
        } else {
          reject(error);
        }
      });

      xhr.addEventListener("timeout", () => {
        if (retryCountRef.current < 2) {
          retryCountRef.current += 1;
          setTimeout(async () => {
            await uploadAudioFile2(formData, true).catch((error) => {
              reject(error);
            });
          }, 2000);
        } else {
          reject(new Error("Upload timed out"));
        }
      });

      xhr.open(
        "POST",
        `${process.env.REACT_APP_SERVER_URL}/recordings/upload`,
        true
      );
      xhr.setRequestHeader("accept", "application/json");
      xhr.send(formData);
    });
  };

  const uploadChunk = async () => {
    isUploadingRef.current = true;
    const chunk = chunks[0];
    console.log(recordingTime)
    console.log(`Uploading chunk ${totalUploadedRef.current + 1} out of ${totalChunkRef.current}`)
    try {
      const formData = new FormData();
      formData.append(
        "file",
        new File([chunk.blob], `chunk_${chunk.timestamp.toISOString()}.mp3`, {
          type: "audio/mp3",
        })
      );
      formData.append("patient_name", patientDetail.name.replace(/\s+/g, "_"));

      const blobName = await uploadAudioFile2(formData);
      const audioFileNames = [...uploadedBlobNames, blobName];
      setUploadedBlobNames(audioFileNames);
      setChunks((prev) => prev.slice(1));
      console.log(chunk);
      console.log(
        `Uploaded chunk from ${chunk.timestamp.toLocaleTimeString()}`
      );
      totalUploadedRef.current += 1
    } catch (error) {
      console.error("Chunk upload failed:", error);
    } finally {
      isUploadingRef.current = false;


    }
  };

  const pauseRecording = async () => {
    if (isRecording && !isPaused && Mp3Recorder) {
      setIsPaused(true);
      clearInterval(timerRef.current);
      clearInterval(chunkIntervalRef.current);
      try {
        await Mp3Recorder.stop();
        if (
          temperoryRecorder.current &&
          temperoryRecorder.current.activeStream
        ) {
          const [buffer, blob] = await temperoryRecorder.current
            .stop()
            .getMp3();
          if (waitingTimerRef.current) {
            clearTimeout(waitingTimerRef.current);
            waitingTimerRef.current = null;
          }
          console.log("Recording is Paused...");
          if (blob.size > 1000) {
            setChunks((prev) => [...prev, { blob, timestamp: new Date() }]);
            totalChunkRef.current += 1;
          } else {
            console.warn("Empty chunk captured");
          }
        }
      } catch (e) {
        console.error("Error pausing recording:", e);
      }
    }
  };

  const resumeRecording = () => {
    if (isRecording && isPaused) {
      console.log("Resuming recording...")
      startRecording()
    }
  };

  const stopRecording = async () => {
    setSessionEndTime(new Date(Date.now()));
    clearInterval(timerRef.current);
    clearInterval(chunkIntervalRef.current);

    try {
      await Mp3Recorder.stop();
      setIsRecording(false);
      if (temperoryRecorder.current && temperoryRecorder.current.activeStream) {
        const [buffer, blob] = await temperoryRecorder.current.stop().getMp3();
        console.log(`Captured chunk of size: ${blob.size} bytes`);
        if (waitingTimerRef.current) {
          clearTimeout(waitingTimerRef.current);
          waitingTimerRef.current = null;
        }

        if (blob.size > 1000) {
          setChunks((prev) => [...prev, { blob, timestamp: new Date() }]);
          totalChunkRef.current += 1;
        } else {
          console.log(blob.size)
          console.warn("Empty chunk captured");
        }
      }

      // Stop the main recorder

      setIsRecordingStarted(false);
      setShowProgressComponent(true);
      setCreateConsultation(true)
      setIsLoading(true);
      console.log("Recording stopped");
    } catch (error) {
      noSleep.current.disable();
      console.error("Stop recording error:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isRecording && chunks.length === 0 && createConsultation) {
      console.log("creating consultation")
      console.log(uploadedBlobNames);
      const audioFileName = uploadedBlobNames?.join(",");
      handleCreateConsultation(patientId, dentistId, audioFileName, uploadedBlobNames);
      setCreateConsultation(false)
    }
  }, [chunks, createConsultation])

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (!patientId) {
      alert("Please Enter Patient Details");
      return;
    }
    console.log(file);
    handleFileChange(file);
  };

  const triggerFileInput = async () => {
    if (!patientId) {
      alert("Please Enter Patient Details");
      return;
    }
    // const patientData = await handleCreatePatient(patientDetail);
    // if (!patientData?.Data?.patient_id) {
    //   return;
    // }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const drawWaveform = () => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording) return;

      requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.lineWidth = 2;
      ctx.strokeStyle = "#0D9488";
      ctx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };

    draw();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleFileChange = async (selectedFile) => {
    console.log(selectedFile);
    if (selectedFile) {
      if (selectedFile.type.includes("audio")) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        await uploadAudioFile(formData).catch((error) => {
          console.error("Upload error for audio file:", error);
          setIsLoading(false);
          setShowProgressComponent(false);
        });
      } else if (selectedFile.type === "text/plain") {
        const formData = new FormData();
        formData.append("file", selectedFile);
        await uploadAudioFile(formData).catch((error) => {
          console.error("Upload error for text file:", error);
          setIsLoading(false);
          setShowProgressComponent(false);
        });
      } else {
        alert("Please upload an audio (.mp3, .wav) or text (.txt) file.");
      }
    }
  };

  const uploadAudioFile = async (formData, isRetry = false) => {
    try {
      if (!isRetry) {
        retryCountRef.current = 0;
      }
      setCurrentFormData(formData);
      setShowProgressComponent(true);
      setUploadProgress(0);
      setIsProcessing(false);
      setProcessingProgress(0);
      setIsLoading(true);
      noSleep.current.enable();
    } catch (error) {
      console.error("Error initializing upload:", error);
      setIsLoading(false);
      setShowProgressComponent(false);
      throw error;
    }
    let P_id = patientId;

    // Get the file from the formData
    const file = formData.get("file");
    const fileSize = file.size;

    // For large files (> 10MB), use chunked upload
    const isLargeFile = fileSize > 10 * 1024 * 1024;

    if (isLargeFile) {
      console.log(
        `Large file detected (${(fileSize / (1024 * 1024)).toFixed(
          2
        )} MB). Using optimized upload.`
      );
    }

    return await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Set timeout to a higher value for large files
      xhr.timeout = isLargeFile ? 900000 : 300000; // 15 minutes for large files, 2 minute for smaller files

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseData = JSON.parse(xhr.responseText);
            console.log("Audio file uploaded successfully!", responseData);

            // Show 100% upload complete briefly before switching to processing
            setUploadProgress(100);

            // Switch to processing state after a short delay
            setTimeout(async () => {
              setIsProcessing(true);
              setProcessingProgress(0);

              // Calculate processing time based on file size
              // Larger files will take longer to process
              const processingInterval = isLargeFile ? 2000 : 1000; // Slower progress for large files

              // Start the processing timer simulation
              const processingTimer = setInterval(() => {
                setProcessingProgress((prev) => {
                  // For large files, progress more slowly
                  const increment = isLargeFile ? 2 : 5;

                  if (prev >= 95) {
                    clearInterval(processingTimer);
                    return 95; // Cap at 95% until actual completion
                  }
                  return prev + increment;
                });
              }, processingInterval);

              // Create consultation in the background
              try {
                console.log(responseData?.blob_name)
                setUploadedBlobNames([responseData?.blob_name])
                await handleCreateConsultation(
                  P_id,
                  dentistId,
                  responseData?.blob_name,
                  [responseData?.blob_name]
                );

                // When processing is complete
                clearInterval(processingTimer);
                setProcessingProgress(100);

                // Show success after a brief delay
                setTimeout(() => {
                  setShowProgressComponent(false);
                  setIsLoading(false);
                  resolve(responseData);
                }, 500);
              } catch (error) {
                clearInterval(processingTimer);
                noSleep.current.disable();
                console.error("Error in post-upload processing:", error);
                setIsLoading(false);
                reject(error);
              }
            }, 500);
          } catch (error) {
            noSleep.current.disable();
            console.error("Error parsing response:", error);
            setIsLoading(false);
            reject(error);
          }
        } else {
          noSleep.current.disable();
          console.error("Error uploading file:", xhr.statusText);
          setIsLoading(false);

          if (retryCountRef.current < 2) {
            console.log("retry attempt", retryCountRef.current);
            retryCountRef.current += 1;
            setTimeout(async () => {
              await uploadAudioFile(formData, true).catch((error) => {
                console.error("Upload error in stopRecording:", error);
                setIsLoading(false);
                setShowProgressComponent(false);
              });
            }, 2000);
          } else {
            setUploadError(xhr.statusText || "Upload failed");
            setShowErrorPopup(true);
          }
          reject(new Error(xhr.statusText));
        }
      });

      xhr.addEventListener("error", (error) => {
        noSleep.current.disable();
        console.error("Error uploading audio file:", error);
        setIsLoading(false);

        if (retryCountRef.current < 2) {
          console.log("retry attempt", retryCountRef.current);
          retryCountRef.current += 1;
          setTimeout(async () => {
            await uploadAudioFile(formData, true).catch((error) => {
              console.error("Upload error in stopRecording:", error);
              setIsLoading(false);
              setShowProgressComponent(false);
            });
          }, 2000);
        } else {
          setUploadError(error.message || "Upload failed");
          setShowErrorPopup(true);
        }
        reject(error);
      });

      xhr.addEventListener("timeout", () => {
        noSleep.current.disable();
        console.error("Upload timed out. Please try again.");
        setIsLoading(false);

        if (retryCountRef.current < 2) {
          retryCountRef.current += 1;
          console.log("retry attempt", retryCountRef.current);
          setTimeout(async () => {
            await uploadAudioFile(formData, true).catch((error) => {
              console.error("Upload error in stopRecording:", error);
              setIsLoading(false);
              setShowProgressComponent(false);
            });
          }, 2000);
        } else {
          setUploadError("Upload timed out");
          setShowErrorPopup(true);
        }
        reject(new Error("Upload timed out"));
      });

      xhr.open(
        "POST",
        `${process.env.REACT_APP_SERVER_URL}/recordings/upload`,
        true
      );
      xhr.setRequestHeader("accept", "application/json");

      // Add additional headers for large files
      if (isLargeFile) {
        xhr.setRequestHeader("X-File-Size", fileSize.toString());
        xhr.setRequestHeader("X-File-Name", file.name);
      }

      xhr.send(formData);
    });
  };

  const handleCreateConsultation = async (
    patientId,
    dentistId,
    audioFileName,
    audioFileNames
  ) => {
    setShowProgressComponent(false);
    setActiveComponent("upload-success");

    const consultationData = {
      dentist_id: dentistId,
      patient_id: patientId,
      recording_fileName: audioFileName,
      recordingSummary1_fileName: "",
      recordingSummary2_fileName: "",
      recording_Summary3_fileName: "",
      finalSummary_fileName: "",
      transcriptedFileName: "",
      isEdited: false,
      consultation_startDate: sessionStartTime,
      consultation_endDate: sessionEndTime,
      sopnotes: "",
      setSurgicalNote: "",
      doctor_letter: "",
    };

    const maxRetries = 3;
    let attempt = 0;
    let success = false;

    while (attempt < maxRetries && !success) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/consultations`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(consultationData),
          }
        );

        if (response.ok) {
          const responseData = await response.json();
          console.log("Consultation created successfully:", responseData);
          setConsultationId(responseData.consultation_id);
          fetchSessions(dentistId);
          setIsLoading(false);
          await processAudio(responseData.consultation_id, audioFileNames);
          setActiveComponent("transcript-processing");
          success = true;
        } else {
          console.warn(
            `Attempt ${attempt + 1} failed:`,
            response.statusText
          );
          attempt++;
          if (attempt >= maxRetries) throw new Error(response.statusText);
        }
      } catch (error) {
        console.error(`Error on attempt ${attempt + 1}:`, error);
        attempt++;
        if (attempt >= maxRetries) {
          noSleep.current.disable();
          setIsLoading(false);
          console.error("Failed to create consultation after 3 attempts.");
        }
      }
    }
  };

  const processAudio = async (consultationId, audioFileNames) => {
    console.log(audioFileNames);
    console.log(consultationId);

    const payload = {
      filenames: audioFileNames,
      consultation_id: consultationId,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUMMARY_SERVER_URL}/summarize-audio/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        noSleep.current.disable();
        throw new Error(
          `HTTP error! Status: ${response.status}`,
          consultationId
        );
      }

      const data = await response.json();
      console.log("Summary is being generated", consultationId);
      console.log(data);
      noSleep.current.disable();
    } catch (error) {
      console.error("Error processing audio:", error);
      noSleep.current.disable();
      return null;
    }
  };

  return (
    <>
      <div className="recording-section">
        {showProgressComponent ? (
          <UploadProgress
            progress={uploadProgress}
            isProcessing={isProcessing}
            processingProgress={processingProgress}
          />
        ) : !isLoading ? (
          <>
            {!isRecording ? (
              <div
                className="aint"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <button className="record-btn" onClick={startRecording}>
                  <Mic className="record-icon" />
                  Start Recording
                </button>
                <h3 className="recording-title">Record session</h3>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt, audio/*"
                  onChange={(event) => handleFileChange(event.target.files[0])}
                  style={{ display: "none" }}
                />
                <p className="recording-text">
                  Drag in or{" "}
                  <button className="upload-btn" onClick={triggerFileInput}>
                    upload
                  </button>{" "}
                  an existing session
                </p>
                {/* <audio src={blobURL} controls="controls" /> */}
              </div>
            ) : (
              <div className="recording-active">
                {isPaused ? (
                  <button
                    className="resume-recording-btn"
                    onClick={resumeRecording}
                  >
                    <Play className="play-icon" />
                    Resume Recording
                  </button>
                ) : (
                  <div className="recording-buttons">
                    <button
                      className="pause-recording-btn"
                      onClick={pauseRecording}
                    >
                      <Pause className="pause-icon" />
                      Pause Recording
                    </button>
                    <button
                      className="stop-recording-btn"
                      onClick={stopRecording}
                    >
                      <Square className="stop-icon" size={16} />
                      Stop Recording
                    </button>
                  </div>
                )}
                <div className="recording-status">
                  <div className="timer-container">
                    <Mic className={`mic-icon ${isPaused ? "paused" : ""}`} />
                    <span className="timer">{formatTime(recordingTime)}</span>
                    <canvas
                      ref={canvasRef}
                      className="waveform"
                      width="100"
                      height="30"
                    />
                  </div>
                </div>
                <div className="recording-message">
                  <h3>Recording....</h3>
                  <p>Stay on this screen while conversing with your patient</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <CircularProgress />
        )}
      </div>

      {showErrorPopup && currentFormData && (
        <CustomPopup
          title="Upload Failed"
          message={`Failed to upload Recording due to slow network. Do you want to retry? ${uploadError}`}
          onClose={() => setShowErrorPopup(false)}
          buttons={[
            {
              text: "Cancel",
              onClick: () => {
                setShowErrorPopup(false);
                setShowProgressComponent(false);
                setIsLoading(false);
              },
            },
            {
              text: "Retry",
              onClick: async () => {
                retryCountRef.current = 0;
                setShowErrorPopup(false);
                await uploadAudioFile(currentFormData).catch((error) => {
                  console.error("Upload error in Recording:", error);
                  setIsLoading(false);
                  setShowProgressComponent(false);
                });
              },
            },
          ]}
        />
      )}
    </>
  );
};

export default RecordingAndUploading;
